import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import Modal from './Modal';
import LoadingSpinner from './LoadingSpinner';
import Card from './Card';
import { fetchTradeUpAvailability, executeVariationTradeUp, executeVariationUpgrade } from '../api/variationTradeUp';
import { getCardRarityClass } from '../utils/cardUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import GLOBALS from '../config/globals';

const { RARITY_DISPLAY, RARITY_TIERS, NEXT_RARITY, TRADE_UP_CARDS_REQUIRED } = GLOBALS;
const RARITY_ORDER = new Map(RARITY_TIERS.map((tier, index) => [tier, index]));

const tradeOptionKey = (variationID, rarity) => `${variationID}-${rarity}`;

const INITIAL_VISIBLE_CARDS = 12;

const EligibleCardGrid = ({ cards, selectedIDs, onToggleCard, variation }) => {
    const [showAll, setShowAll] = useState(false);
    const sorted = [...cards].sort((a, b) => (a.rating ?? 0) - (b.rating ?? 0));
    const visible = showAll ? sorted : sorted.slice(0, INITIAL_VISIBLE_CARDS);
    const hiddenCount = sorted.length - INITIAL_VISIBLE_CARDS;

    return (
        <div className="trade-up-modal__card-grid">
            {visible.map(card => {
                const isSelected = selectedIDs.has(String(card.inventoryID));
                const isMaxed = selectedIDs.size >= TRADE_UP_CARDS_REQUIRED && !isSelected;
                return (
                    <button
                        key={card.inventoryID}
                        className={`trade-up-modal__card-item ${isSelected ? 'trade-up-modal__card-item--selected' : ''}`}
                        onClick={() => onToggleCard(card.inventoryID)}
                        disabled={isMaxed}
                        type="button"
                    >
                        <Card card={card} variation={variation} showLabel />
                    </button>
                );
            })}
            {!showAll && hiddenCount > 0 && (
                <button
                    className="trade-up-modal__show-more"
                    onClick={() => setShowAll(true)}
                    type="button"
                >
                    Show {hiddenCount} more
                </button>
            )}
        </div>
    );
};

EligibleCardGrid.propTypes = {
    cards: PropTypes.array.isRequired,
    selectedIDs: PropTypes.instanceOf(Set).isRequired,
    onToggleCard: PropTypes.func.isRequired,
    variation: PropTypes.shape({
        name: PropTypes.string,
        cssClass: PropTypes.string,
    }),
};

const TradeUpOptionRow = ({
    tradeOption, isExpanded, onToggleExpand, selectedIDs, onToggleCard,
    activeTradeKey, onTrade, onTradeAll, onUpgrade, onUpgradeAll,
}) => {
    const key = tradeOptionKey(tradeOption.variationID, tradeOption.rarity);
    const fromDisplay = RARITY_DISPLAY[tradeOption.rarity];
    const nextRarity = NEXT_RARITY[tradeOption.rarity];
    const toDisplay = RARITY_DISPLAY[nextRarity] || RARITY_DISPLAY.uranium;
    const isTrading = activeTradeKey === key;
    const selectionCount = selectedIDs.size;
    const upgradesAvailable = tradeOption.upgradesAvailable ?? 0;
    const canTrade = selectionCount === TRADE_UP_CARDS_REQUIRED && tradeOption.tradesAvailable > 0;
    const canTradeAll = tradeOption.tradesAvailable > 0;
    const canUpgrade = selectionCount === TRADE_UP_CARDS_REQUIRED && upgradesAvailable > 0;
    const canUpgradeAll = upgradesAvailable > 0;
    const variationForCards = { name: tradeOption.variationName, cssClass: tradeOption.variationCssClass };

    return (
        <div className={`trade-up-modal__option ${isExpanded ? 'trade-up-modal__option--expanded' : ''}`}>
            <button className="trade-up-modal__row" onClick={onToggleExpand} type="button">
                <span className="trade-up-modal__variation">
                    {isExpanded ? '▼' : '▶'} {tradeOption.variationName}
                </span>
                <span className={`trade-up-modal__rarity ${getCardRarityClass(fromDisplay.rating)}`}>
                    {fromDisplay.label}
                </span>
                <span className={`trade-up-modal__rarity ${getCardRarityClass(toDisplay.rating)}`}>
                    {toDisplay.label}
                </span>
                <span className="trade-up-modal__eligible">{tradeOption.eligibleCount}</span>
                <span>{tradeOption.tradesAvailable} / {upgradesAvailable}</span>
            </button>

            {isExpanded && (
                <div className="trade-up-modal__expanded">
                    <div className="trade-up-modal__selection-header">
                        <span className="trade-up-modal__selection-count">
                            {selectionCount} / {TRADE_UP_CARDS_REQUIRED} selected
                        </span>
                        <div className="trade-up-modal__btn-group">
                            <span className="trade-up-modal__btn-wrapper">
                                <button
                                    className="trade-up-modal__btn"
                                    onClick={() => onTrade(tradeOption.variationID, tradeOption.rarity)}
                                    disabled={!canTrade || !!activeTradeKey}
                                    type="button"
                                >
                                    {isTrading ? '...' : 'Trade Up'}
                                </button>
                                <span className="trade-up-modal__btn-tooltip">
                                    Move this variation up one rarity tier. 3 selected cards lose the variation; a random base card you own in the next tier gets it.
                                </span>
                            </span>
                            <span className="trade-up-modal__btn-wrapper">
                                <button
                                    className="trade-up-modal__btn trade-up-modal__btn--all"
                                    onClick={() => onTradeAll(tradeOption.variationID, tradeOption.rarity)}
                                    disabled={!canTradeAll || !!activeTradeKey}
                                    type="button"
                                >
                                    {isTrading ? '...' : `Trade All (${tradeOption.tradesAvailable})`}
                                </button>
                                <span className="trade-up-modal__btn-tooltip">
                                    Move this variation up one rarity tier. 3 selected cards lose the variation; a random base card you own in the next tier gets it. (For all possible cards.)
                                </span>
                            </span>
                            <span className="trade-up-modal__btn-wrapper">
                                <button
                                    className="trade-up-modal__btn trade-up-modal__btn--upgrade"
                                    onClick={() => onUpgrade(tradeOption.variationID, tradeOption.rarity)}
                                    disabled={!canUpgrade || !!activeTradeKey}
                                    type="button"
                                >
                                    {isTrading ? '...' : 'Upgrade'}
                                </button>
                                <span className="trade-up-modal__btn-tooltip">
                                    Keep the same rarity but roll for a rarer variation. 3 selected cards lose their variation; one of the 3 gets the new one (weighted by its series drop rates).
                                </span>
                            </span>
                            <span className="trade-up-modal__btn-wrapper">
                                <button
                                    className="trade-up-modal__btn trade-up-modal__btn--upgrade-all"
                                    onClick={() => onUpgradeAll(tradeOption.variationID, tradeOption.rarity)}
                                    disabled={!canUpgradeAll || !!activeTradeKey}
                                    type="button"
                                >
                                    {isTrading ? '...' : `Upgrade All (${upgradesAvailable})`}
                                </button>
                                <span className="trade-up-modal__btn-tooltip">
                                    Keep the same rarity but roll for a rarer variation. 3 selected cards lose their variation; one of the 3 gets the new one (weighted by its series drop rates). (For all possible cards.)
                                </span>
                            </span>
                        </div>
                    </div>
                    <EligibleCardGrid
                        cards={tradeOption.eligibleCards || []}
                        selectedIDs={selectedIDs}
                        onToggleCard={onToggleCard}
                        variation={variationForCards}
                    />
                </div>
            )}
        </div>
    );
};

TradeUpOptionRow.propTypes = {
    tradeOption: PropTypes.object.isRequired,
    isExpanded: PropTypes.bool.isRequired,
    onToggleExpand: PropTypes.func.isRequired,
    selectedIDs: PropTypes.instanceOf(Set).isRequired,
    onToggleCard: PropTypes.func.isRequired,
    activeTradeKey: PropTypes.string,
    onTrade: PropTypes.func.isRequired,
    onTradeAll: PropTypes.func.isRequired,
    onUpgrade: PropTypes.func.isRequired,
    onUpgradeAll: PropTypes.func.isRequired,
};

const VariationTradeUpModal = ({ onClose, onTradeComplete }) => {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTradeKey, setActiveTradeKey] = useState(null);
    const [expandedKey, setExpandedKey] = useState(null);
    const [selectedByOption, setSelectedByOption] = useState({});

    const loadAvailability = useCallback(() => {
        setLoading(true);
        fetchTradeUpAvailability()
            .then(data => setOptions(data.options || []))
            .catch(error => {
                console.error('Failed to load trade-up options:', error);
                toast.error('Failed to load trade-up options');
                setOptions([]);
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { loadAvailability(); }, [loadAvailability]);

    const handleToggleExpand = (optionKey) => {
        setExpandedKey(prev => prev === optionKey ? null : optionKey);
    };

    const handleToggleCard = (optionKey, inventoryID) => {
        setSelectedByOption(prev => {
            const currentSet = prev[optionKey] || new Set();
            const newSet = new Set(currentSet);
            const idString = String(inventoryID);

            if (newSet.has(idString)) {
                newSet.delete(idString);
            } else if (newSet.size < TRADE_UP_CARDS_REQUIRED) {
                newSet.add(idString);
            }

            return { ...prev, [optionKey]: newSet };
        });
    };

    const submitTrade = useCallback(async (variationID, rarity, inventoryIDs) => {
        const optionKey = tradeOptionKey(variationID, rarity);
        setActiveTradeKey(optionKey);
        try {
            const result = await executeVariationTradeUp(variationID, rarity, inventoryIDs);
            toast.success(`${result.tradesCompleted} trade${result.tradesCompleted > 1 ? 's' : ''} completed!`);
            onTradeComplete(result);
            onClose();
        } catch (error) {
            console.error('Trade-up failed:', error);
            toast.error(error?.response?.data?.error || error?.message || 'Trade failed');
        } finally {
            setActiveTradeKey(null);
        }
    }, [onTradeComplete, onClose]);

    const handleTrade = useCallback((variationID, rarity) => {
        const optionKey = tradeOptionKey(variationID, rarity);
        const selectedIDs = selectedByOption[optionKey];
        if (!selectedIDs || selectedIDs.size !== TRADE_UP_CARDS_REQUIRED) return;
        submitTrade(variationID, rarity, [...selectedIDs]);
    }, [selectedByOption, submitTrade]);

    const handleTradeAll = useCallback((variationID, rarity) => {
        submitTrade(variationID, rarity);
    }, [submitTrade]);

    const submitUpgrade = useCallback(async (variationID, rarity, inventoryIDs) => {
        const optionKey = tradeOptionKey(variationID, rarity);
        setActiveTradeKey(optionKey);
        try {
            const result = await executeVariationUpgrade(variationID, rarity, inventoryIDs);
            onTradeComplete({
                results: [{
                    inventoryID: result.winnerInventoryID,
                    Card: result.winnerCard,
                    CardVariation: result.newVariation,
                }],
            });
            onClose();
        } catch (error) {
            console.error('Upgrade failed:', error);
            toast.error(error?.response?.data?.error || error?.message || 'Upgrade failed');
        } finally {
            setActiveTradeKey(null);
        }
    }, [onTradeComplete, onClose]);

    const handleUpgrade = useCallback((variationID, rarity) => {
        const optionKey = tradeOptionKey(variationID, rarity);
        const selectedIDs = selectedByOption[optionKey];
        if (!selectedIDs || selectedIDs.size !== TRADE_UP_CARDS_REQUIRED) return;
        submitUpgrade(variationID, rarity, [...selectedIDs]);
    }, [selectedByOption, submitUpgrade]);

    const runUpgradeBatch = useCallback(async (variationID, rarity) => {
        const optionKey = tradeOptionKey(variationID, rarity);
        const option = options.find(o => tradeOptionKey(o.variationID, o.rarity) === optionKey);
        if (!option) return;
        const sortedCards = [...(option.eligibleCards || [])].sort((a, b) => (a.rating ?? 0) - (b.rating ?? 0));
        const count = option.upgradesAvailable ?? 0;
        if (count === 0) return;

        setActiveTradeKey(optionKey);
        const collectedResults = [];
        try {
            for (let i = 0; i < count; i++) {
                const batch = sortedCards.slice(i * TRADE_UP_CARDS_REQUIRED, (i + 1) * TRADE_UP_CARDS_REQUIRED);
                if (batch.length < TRADE_UP_CARDS_REQUIRED) break;
                const inventoryIDs = batch.map(card => String(card.inventoryID));
                const result = await executeVariationUpgrade(variationID, rarity, inventoryIDs);
                collectedResults.push({
                    inventoryID: result.winnerInventoryID,
                    Card: result.winnerCard,
                    CardVariation: result.newVariation,
                });
            }
            onTradeComplete({ results: collectedResults });
            onClose();
        } catch (error) {
            console.error('Upgrade-all failed:', error);
            toast.error(error?.response?.data?.error || error?.message || 'Upgrade failed');
        } finally {
            setActiveTradeKey(null);
        }
    }, [options, onTradeComplete, onClose]);

    const handleUpgradeAll = useCallback((variationID, rarity) => {
        runUpgradeBatch(variationID, rarity);
    }, [runUpgradeBatch]);

    const renderContent = () => {
        if (loading) return <LoadingSpinner />;

        if (options.length === 0) {
            return <p className="trade-up-modal__empty">No variations available to trade up.</p>;
        }

        return (
            <div className="trade-up-modal__table">
                <div className="trade-up-modal__header-row">
                    <span>Variation</span>
                    <span>From</span>
                    <span>To</span>
                    <span className="trade-up-modal__eligible">Eligible</span>
                    <span>Trades / Upgrades</span>
                </div>
                {[...options].sort((a, b) => (RARITY_ORDER.get(a.rarity) ?? 0) - (RARITY_ORDER.get(b.rarity) ?? 0)).map(tradeOption => {
                    const optionKey = tradeOptionKey(tradeOption.variationID, tradeOption.rarity);
                    return (
                        <TradeUpOptionRow
                            key={optionKey}
                            tradeOption={tradeOption}
                            isExpanded={expandedKey === optionKey}
                            onToggleExpand={() => handleToggleExpand(optionKey)}
                            selectedIDs={selectedByOption[optionKey] || new Set()}
                            onToggleCard={(inventoryID) => handleToggleCard(optionKey, inventoryID)}
                            activeTradeKey={activeTradeKey}
                            onTrade={handleTrade}
                            onTradeAll={handleTradeAll}
                            onUpgrade={handleUpgrade}
                            onUpgradeAll={handleUpgradeAll}
                        />
                    );
                })}
            </div>
        );
    };

    return (
        <Modal onClose={onClose} title="Trade Up Variations" className="trade-up-modal">
            <div className="trade-up-modal__content">
                <p className="trade-up-modal__subtitle">
                    Select {TRADE_UP_CARDS_REQUIRED} cards with the same variation to trade up a tier or upgrade the variation.
                    <span className="trade-up-modal__info-tooltip">
                        <FontAwesomeIcon icon={faCircleInfo} />
                        <span className="trade-up-modal__info-tooltip-text">
                            <strong>Trade Up:</strong> variation is removed from all {TRADE_UP_CARDS_REQUIRED} and applied to a random base card you own in the next rarity tier.
                            <br /><br />
                            <strong>Upgrade:</strong> variation is removed from all {TRADE_UP_CARDS_REQUIRED} and one of them is re-stamped with a rarer variation (weighted roll based on its series drop rates).
                        </span>
                    </span>
                </p>
                {renderContent()}
            </div>
        </Modal>
    );
};

VariationTradeUpModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    onTradeComplete: PropTypes.func.isRequired,
};

export default VariationTradeUpModal;
